// React
import { useEffect, useMemo, useState } from "react"

/**
 * Luxon
 */
import {
	DateTime,
	Duration,
	DateTimeUnit,
	Settings,
	DateObjectUnits,
} from "luxon"
import { ms, parse, parseStrict, format } from "./ms"
import type { StringValue } from "./ms"

// Vars
import { DEFAULT_LANG, DEFAULT_TIMEZONE } from "./env"

// Set default timezone and locale
Settings.defaultZone = DEFAULT_TIMEZONE
Settings.defaultLocale = DEFAULT_LANG

export const today = DateTime.now()

// Transforms "EUROPE_AMSTERDAM" into "Europe/Amsterdam"
export function transformTimezoneConstantToJSTimezone(
	inputString: string | undefined,
): string {
	if (inputString === undefined) {
		return DEFAULT_TIMEZONE as string
	} else {
		return inputString
			.split("_")
			.map(
				(word) =>
					word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
			)
			.join("/")
	}
}

// Create a simple event emitter for locale changes
const localeChangeListeners = new Set<(locale: string) => void>()

// Allow to set locale with event notification
export function setLocale(locale: string) {
	Settings.defaultLocale = locale
	// Notify all listeners about the locale change
	localeChangeListeners.forEach((listener) => listener(locale))
}

/**
 * useCurrentDate
 *
 * Uses the current date rounded down to every 15mins
 * @param interval
 * @returns
 */
export function useCurrentDate(interval = ms("15m")) {
	const [now, set] = useState<DateTime>(DateTime.now())

	useEffect(() => {
		const work = () => {
			set(DateTime.now())
		}

		const timeout = setInterval(work, interval)
		return () => {
			clearInterval(timeout)
		}
	}, [interval])

	return now
}

// Hook for locale-aware DateTime that updates when locale changes TODO: rename
export function useLocalizedDateTime() {
	const [currentLocale, setCurrentLocale] = useState(Settings.defaultLocale)

	useEffect(() => {
		// Listen for locale changes
		const handleLocaleChange = (newLocale: string) => {
			setCurrentLocale(newLocale)
		}

		localeChangeListeners.add(handleLocaleChange)

		// Cleanup listener on unmount
		return () => {
			localeChangeListeners.delete(handleLocaleChange)
		}
	}, [])

	return useMemo(() => {
		return {
			// Factory functions that use the current locale
			now: () => DateTime.now().setLocale(currentLocale),
			fromISO: (iso: string) =>
				DateTime.fromISO(iso).setLocale(currentLocale),
			fromMillis: (millis: number) =>
				DateTime.fromMillis(millis).setLocale(currentLocale),
			fromJSDate: (date: Date) =>
				DateTime.fromJSDate(date).setLocale(currentLocale),
			fromObject: (obj: DateObjectUnits) =>
				DateTime.fromObject(obj).setLocale(currentLocale),
		}
	}, [currentLocale])
}

// Export default from Luxon
export { DateTime, Duration, ms, parse, parseStrict, format }

// Types
export type { DateTimeUnit, StringValue }
